import { FC, useMemo } from 'react';
import Checkbox from '@/components/UI/FormElements/Checkbox/Checkbox';
import { MetadataGlossaryV1 } from '@/model/Metadata.model';
import { SamplingSheetColumnId } from '@/model/SamplePlan.model';
import { _isNotEmpty, _notNil } from '@/littledash';
import { BaseColumns, ColumnOption, columnComparator } from './ExecuteSamples.utils';
import styles from './SamplingSheetColumnSelector.module.scss';

interface SamplingSheetColumnSelectorProps {
  sampleMetadata: Array<MetadataGlossaryV1>;
  selectedColumns: Set<SamplingSheetColumnId>;
  requiredColumns: Set<SamplingSheetColumnId>;
  onChange: (columnId: SamplingSheetColumnId, selected: boolean) => void;
}

const SamplingSheetColumnSelector: FC<SamplingSheetColumnSelectorProps> = ({
  sampleMetadata,
  selectedColumns,
  requiredColumns,
  onChange,
}) => {
  const { shownColumns, hiddenColumns } = useMemo(() => {
    const baseColumns = Object.values(BaseColumns);

    const metadataColumns = sampleMetadata.reduce<Array<ColumnOption>>((acc, meta) => {
      if (_notNil(meta.api_id)) {
        acc.push({ id: meta.api_id, label: meta.title });
      }
      return acc;
    }, []);

    const allColumns = [...baseColumns, ...metadataColumns];

    const { shown, hidden } = allColumns.reduce<{ shown: Array<ColumnOption>; hidden: Array<ColumnOption> }>(
      (acc, column) => {
        const isRequired = requiredColumns.has(column.id);
        if (isRequired || selectedColumns.has(column.id)) {
          acc.shown.push({ ...column, required: isRequired });
        } else {
          acc.hidden.push(column);
        }
        return acc;
      },
      { shown: [], hidden: [] }
    );

    return {
      shownColumns: shown.sort(columnComparator),
      hiddenColumns: hidden.sort(columnComparator),
    };
  }, [sampleMetadata, selectedColumns, requiredColumns]);

  const handleToggle = (columnId: SamplingSheetColumnId) => {
    onChange(columnId, !selectedColumns.has(columnId));
  };

  return (
    <div data-test-component="SamplingSheetColumnSelector" data-test-element="container">
      <div className="mb3">
        <label className="db mb2" data-testid="column-selector-title">
          Select columns to include in the sampling sheet
        </label>
        <p className="f6 dark-gray ma0" data-testid="column-selector-description">
          Columns can be edited in the sheet later.
        </p>
      </div>

      <div className={`ba b--moon-gray br2 overflow-y-auto ${styles.columnList}`} data-test-element="column-list">
        {_isNotEmpty(shownColumns) && (
          <div className="bg-near-white pa2 bb b--moon-gray" data-test-element="shown-header">
            <span className="fw5 f6" data-testid="shown-columns-header">
              Shown columns
            </span>
          </div>
        )}

        {shownColumns.map((column) => (
          <div
            key={column.id}
            className="pa3 bb b--moon-gray flex items-center"
            data-test-element="column-row"
            data-test-key={column.id}
          >
            <Checkbox
              id={`column-${column.id}`}
              name={column.id}
              checked={true}
              disabled={column.required}
              onChange={() => handleToggle(column.id)}
              label={column.label}
              className="w-100"
              testPrefix={`column-${column.id}`}
            />
          </div>
        ))}

        {_isNotEmpty(hiddenColumns) && (
          <>
            <div className="bg-near-white pa2 bb b--moon-gray" data-test-element="hidden-header">
              <span className="fw5 f6" data-testid="hidden-columns-header">
                Hidden columns
              </span>
            </div>

            {hiddenColumns.map((column) => (
              <div
                key={column.id}
                className="pa3 bb b--moon-gray flex items-center"
                data-test-element="column-row"
                data-test-key={column.id}
              >
                <Checkbox
                  id={`column-${column.id}`}
                  name={column.id}
                  checked={false}
                  onChange={() => handleToggle(column.id)}
                  label={column.label}
                  className="w-100"
                  testPrefix={`column-${column.id}`}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default SamplingSheetColumnSelector;
